import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiServices from '../../services/service';
import { Ic } from '../../shared/component/GosIcon';

/* GrowthOS — Action Permissions (design: growthos/AccessPermissions.jsx →
   ActionPermissions). Header + toolbar (search + Module/Action filters) + table
   (Name / Module / Permission Set + per-row Edit) + Add/Edit modal. The design is
   a mock; wired to the REAL permission APIs (getPermissionList,
   getUnListedPermissions, getPermissionModuleList, getPermissionSetList,
   createPermissions, updatePermissions). There is NO delete-permission API, so
   the design's per-row Delete is dropped (Edit only). The "Action type" column is
   DERIVED from the permission name suffix (View/Create/Update/Delete/Export) — not
   a stored field. Reuses the shared .gos-tm scope + .gos-import-* modal shell — no
   new CSS. Renders in-shell via the `embedded` prop (Settings › Organization). */

const AVA_COLORS = ['#c96342', '#2e63a6', '#6a4fae', '#2c7a45', '#b34480', '#9a751c', '#4f6b8f', '#0e8c6f', '#2f7ea3', '#a35a2f'];
const hashIdx = (s, n) => {
  let h = 0;
  for (let i = 0; i < (s || '').length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % n;
};
const tintFor = (s) => AVA_COLORS[hashIdx(String(s || ''), AVA_COLORS.length)];

const ACT_TYPES = [
  { id: 'View', icon: 'eye', color: '#2e63a6' },
  { id: 'Create', icon: 'plus', color: '#2c7a45' },
  { id: 'Update', icon: 'edit', color: '#9a751c' },
  { id: 'Delete', icon: 'trash', color: '#c0473f' },
  { id: 'Export', icon: 'download', color: '#6a4fae' },
];
/* Real permission names are CQRS-style (GetXxxQuery, XxxRequest, GetSaleOrderPdfRequest,
   UpdateXxxCommand, …) — they don't end in View/Create/Update/Delete/Export, so match by
   keyword instead. Order matters: check the write/export verbs before the broad get/query
   read fallback (e.g. "GetSaleOrderPdfRequest" → Export via "pdf", not View via "get"). */
const typeOf = (name) => {
  const n = String(name || '').toLowerCase();
  if (/(delete|remove|deactivate|disable|revoke)/.test(n)) return 'Delete';
  if (/(export|download|pdf|print|report)/.test(n)) return 'Export';
  if (/(update|edit|modify|change|assign|enable|activate|approve|reject|send|move|convert|map|refresh)/.test(n)) return 'Update';
  if (/(create|add|insert|new|register|import|upload|generate|clone|duplicate|save|post)/.test(n)) return 'Create';
  if (/(get|list|query|view|read|fetch|search|detail|lookup|exist|count|validate)/.test(n)) return 'View';
  return 'View';
};
const typeMeta = (t) => ACT_TYPES.find((x) => x.id === t) || ACT_TYPES[0];

const nameOfPerm = (p) => p?.permissionValue || p?.permissionDisplayName || p?.permissionSetName || '—';

/* react-select styling to match the GrowthOS themed inputs */
const gosSelect = {
  control: (base, s) => ({
    ...base, minHeight: 42, borderRadius: 10, fontSize: 13.5,
    borderColor: s.isFocused ? 'var(--gos-accent)' : 'var(--gos-border-strong)',
    boxShadow: s.isFocused ? '0 0 0 3px var(--gos-accent-soft)' : 'none',
    backgroundColor: 'var(--gos-surface)', '&:hover': { borderColor: 'var(--gos-accent)' },
  }),
  menu: (b) => ({ ...b, zIndex: 9999, backgroundColor: 'var(--gos-surface)', border: '1px solid var(--gos-border-strong)', borderRadius: 11, overflow: 'hidden' }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  option: (b, s) => ({ ...b, fontSize: 13.5, backgroundColor: s.isSelected ? 'var(--gos-accent-soft)' : s.isFocused ? 'var(--gos-surface-3)' : 'transparent', color: s.isSelected ? 'var(--gos-accent-ink)' : 'var(--gos-text)' }),
  placeholder: (b) => ({ ...b, color: 'var(--gos-text-3)' }),
  singleValue: (b) => ({ ...b, color: 'var(--gos-text)' }),
  input: (b) => ({ ...b, color: 'var(--gos-text)' }),
  indicatorSeparator: (b) => ({ ...b, backgroundColor: 'var(--gos-border)' }),
  dropdownIndicator: (b) => ({ ...b, color: 'var(--gos-text-3)' }),
};
/* compact filter variant */
const gosSelectSm = { ...gosSelect, control: (base, s) => ({ ...gosSelect.control(base, s), minHeight: 38, fontSize: 13 }) };

const reqStar = <span style={{ color: 'var(--gos-red-fg)' }}> *</span>;

/* Add / Edit modal — preserves the real create/update flow (unlisted permission
   name + module + set → createPermissions; edit re-selects module + set →
   updatePermissions). */
function ActModal({ open, record, unlisted, moduleList, setList, onClose, onSave, saving }) {
  const isEdit = !!record;
  const [name, setName] = useState(null); // {value,label} from unlisted (add only)
  const [module, setModule] = useState(null); // {value:id,label:displayName}
  const [set, setSet] = useState(null); // {value:id,label:displayName}
  const [err, setErr] = useState({});

  const moduleOptions = useMemo(
    () => (moduleList || []).map((m) => ({ value: m.id, label: m.displayName })),
    [moduleList]
  );
  const setOptions = useMemo(
    () => (setList || []).map((s) => ({ value: s.id, label: s.displayName })),
    [setList]
  );
  const nameOptions = useMemo(
    () => (unlisted || []).map((u) => ({ value: u.value, label: u.description })),
    [unlisted]
  );

  useEffect(() => {
    if (!open) return;
    setErr({});
    if (record) {
      setName({ value: record.permissionValue, label: nameOfPerm(record) });
      setModule(moduleOptions.find((m) => m.label === record.moduleName) || null);
      setSet(setOptions.find((s) => s.label === record.permissionSetName) || null);
    } else {
      setName(null); setModule(null); setSet(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record]);

  if (!open) return null;

  const submit = () => {
    const e = {};
    if (!name) e.name = true;
    if (!module) e.module = true;
    if (!set) e.set = true;
    if (Object.keys(e).length) { setErr(e); return; }
    if (isEdit) {
      onSave({
        permissionId: record.permissionId,
        permissionValue: record.permissionValue ?? name.label,
        permissionSetName: set.label,
        permissionSetId: set.value,
        module: module.label,
        ModuleId: module.value,
        permissionGroupId: record.permissionGroupId || '',
        permissionGroupDisplayName: record.permissionGroupDisplayName || '',
      }, true);
    } else {
      onSave({
        permissionId: ' ',
        permissionValue: name.label,
        permissionSetName: set.label,
        permissionSetId: set.value,
        module: module.label,
        ModuleId: module.value,
        permissionGroupId: '',
        permissionGroupDisplayName: '',
        type: 'add',
      }, false);
    }
  };

  const tm = typeMeta(typeOf(name?.label));

  return (
    <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="gos-import-modal" style={{ maxWidth: 520 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            <span className="gos-import-step-ic" style={{ background: `${tm.color}1f`, color: tm.color }}><Ic name="lock" size={18} /></span>
            <div>
              <div className="gos-import-title">{isEdit ? 'Edit action permission' : 'Add action permission'}</div>
              <div className="gos-import-sub">Define an action a permission set can perform on a module.</div>
            </div>
          </div>
          <button type="button" className="gos-import-close" onClick={onClose} disabled={saving}><Ic name="x" size={18} /></button>
        </div>

        <div className="gos-import-body" style={{ padding: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <label className="gos-tm-flabel">Permission name{reqStar}</label>
            {isEdit ? (
              <input className="gos-tm-input" value={nameOfPerm(record)} disabled readOnly />
            ) : (
              <Select
                options={nameOptions} value={name}
                onChange={(o) => { setName(o); if (err.name) setErr((p) => ({ ...p, name: false })); }}
                placeholder="Select a permission…" menuPortalTarget={document.body} styles={gosSelect}
              />
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="gos-tm-flabel">Module{reqStar}</label>
            <Select
              options={moduleOptions} value={module}
              onChange={(o) => { setModule(o); if (err.module) setErr((p) => ({ ...p, module: false })); }}
              placeholder="Select module" menuPortalTarget={document.body} styles={gosSelect}
            />
          </div>

          <div>
            <label className="gos-tm-flabel">Permission set{reqStar}</label>
            <Select
              options={setOptions} value={set}
              onChange={(o) => { setSet(o); if (err.set) setErr((p) => ({ ...p, set: false })); }}
              placeholder="Select permission set" menuPortalTarget={document.body} styles={gosSelect}
            />
          </div>
        </div>

        <div className="gos-import-foot end">
          <div className="gos-foot-actions">
            <button type="button" className="gos-btn gos-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" className="gos-btn gos-btn-primary" onClick={submit} disabled={saving}>
              <Ic name="check" size={16} />{saving ? 'Saving…' : isEdit ? 'Save' : 'Add permission'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionPermissionList({ embedded = false }) {
  const Wrap = ({ children }) => (embedded ? <>{children}</> : <div className="inner-page">{children}</div>);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [modFilter, setModFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);

  const [unlisted, setUnlisted] = useState([]);
  const [moduleList, setModuleList] = useState([]);
  const [permSetList, setPermSetList] = useState([]);

  const [modal, setModal] = useState(null); // null | {} (add) | { record } (edit)
  const [saving, setSaving] = useState(false);

  const loadList = () => {
    setLoading(true);
    apiServices
      .getPermissionList()
      .then((res) => setList(res.data?.permissionList || []))
      .catch((e) => console.error('getPermissionList', e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadList();
    apiServices.getUnListedPermissions().then((r) => setUnlisted(r.data?.requestAndQueryNameList || [])).catch((e) => console.error('getUnListedPermissions', e));
    apiServices.getPermissionModuleList().then((r) => setModuleList(r.data?.authReferenceLookups || [])).catch((e) => console.error('getPermissionModuleList', e));
    apiServices.getPermissionSetList().then((r) => setPermSetList(r.data?.authReferenceLookups || [])).catch((e) => console.error('getPermissionSetList', e));
  }, []);

  const moduleOptions = useMemo(() => {
    const names = [...new Set(list.map((p) => p.moduleName).filter(Boolean))].sort();
    return names.map((n) => ({ value: n, label: n }));
  }, [list]);
  const typeOptions = useMemo(() => ACT_TYPES.map((t) => ({ value: t.id, label: t.id })), []);

  const rows = useMemo(() => {
    let r = list;
    const s = q.trim().toLowerCase();
    if (s) r = r.filter((p) => `${nameOfPerm(p)} ${p.moduleName || ''} ${p.permissionSetName || ''}`.toLowerCase().includes(s));
    if (modFilter) r = r.filter((p) => p.moduleName === modFilter.value);
    if (typeFilter) r = r.filter((p) => typeOf(nameOfPerm(p)) === typeFilter.value);
    return r;
  }, [list, q, modFilter, typeFilter]);

  const save = (payload, isEdit) => {
    setSaving(true);
    const api = isEdit ? apiServices.updatePermissions : apiServices.createPermissions;
    const body = { ...payload };
    if (!isEdit) delete body.type;
    api(body)
      .then((response) => {
        if (response.status === 200) {
          toast.success(isEdit ? 'Action permission updated successfully' : 'Action permission created successfully', { position: 'top-right' });
          setModal(null);
          loadList();
          // a newly-listed permission drops off the unlisted set — refresh it
          apiServices.getUnListedPermissions().then((r) => setUnlisted(r.data?.requestAndQueryNameList || [])).catch(() => {});
        } else {
          const errs = response.errors || {};
          Object.keys(errs).forEach((k) => (errs[k] || []).forEach((m) => toast.error(m, { position: 'top-right' })));
        }
      })
      .catch((error) => {
        const errs = error?.response?.data?.errors;
        if (errs) Object.keys(errs).forEach((k) => (errs[k] || []).forEach((m) => toast.error(m, { position: 'top-right' })));
        else toast.error(error?.response?.data?.message || error.message || 'An unexpected error occurred', { position: 'top-right' });
      })
      .finally(() => setSaving(false));
  };

  return (
    <Wrap>
      <div className="gos-tm">
        <div className="gos-tm-head">
          <div>
            <div className="gos-tm-title">Action Permissions</div>
            <div className="gos-tm-sub">Actions a permission set can perform on a module. Assign these to roles under Teams &amp; Permissions.</div>
          </div>
          <button type="button" className="gos-btn gos-btn-primary" onClick={() => setModal({})}>
            <Ic name="plus" size={16} />Add Action Permission
          </button>
        </div>

        <div className="gos-tm-cardbox" style={{ overflow: 'visible', padding: 0 }}>
          <div className="gos-tm-toolbar" style={{ padding: '14px 16px', borderBottom: '1px solid var(--gos-border)', margin: 0 }}>
            <div className="gos-tm-search">
              <Ic name="search" size={16} />
              <input placeholder="Search permissions…" value={q} onChange={(e) => setQ(e.target.value)} />
              {q ? <button type="button" className="gos-tm-clear" onClick={() => setQ('')}><Ic name="x" size={14} /></button> : null}
            </div>
            <span className="gos-tm-count"><strong>{rows.length}</strong> permissions</span>
            <div className="gos-tm-grow" />
            <div style={{ width: 190 }}>
              <Select
                options={moduleOptions} value={modFilter} onChange={setModFilter}
                placeholder="All modules" isClearable menuPortalTarget={document.body} styles={gosSelectSm}
              />
            </div>
            <div style={{ width: 160 }}>
              <Select
                options={typeOptions} value={typeFilter} onChange={setTypeFilter}
                placeholder="All actions" isClearable menuPortalTarget={document.body} styles={gosSelectSm}
              />
            </div>
          </div>

          {loading ? (
            <div className="gos-tm-tblscroll">
              <table className="gos-tm-tbl">
                <tbody>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      <td><div className="gos-tm-cellead"><span className="gos-skel-an" style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0 }} /><span className="gos-skel-an" style={{ width: 150, height: 10, borderRadius: 5 }} /></div></td>
                      <td><span className="gos-skel-an" style={{ display: 'block', width: 130, height: 10, borderRadius: 5 }} /></td>
                      <td><span className="gos-skel-an" style={{ display: 'block', width: 110, height: 10, borderRadius: 5 }} /></td>
                      <td />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              <div className="gos-tm-tblscroll">
                <table className="gos-tm-tbl">
                  <thead>
                    <tr><th>Name</th><th>Module</th><th>Permission Set</th>{/* <th style={{ width: 56, textAlign: 'center' }}>Edit</th> */}</tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => {
                      const tm = typeMeta(typeOf(nameOfPerm(p)));
                      return (
                        <tr key={p.permissionId || nameOfPerm(p)}>
                          <td>
                            <div className="gos-tm-cellead">
                              <span style={{ width: 28, height: 28, borderRadius: 8, display: 'grid', placeItems: 'center', flexShrink: 0, background: `${tm.color}1f`, color: tm.color }}>
                                <Ic name={tm.icon} size={15} />
                              </span>
                              <span className="gos-tm-cellname">{nameOfPerm(p)}</span>
                            </div>
                          </td>
                          <td>
                            <span className="gos-tm-row" style={{ gap: 8 }}>
                              <span style={{ width: 9, height: 9, borderRadius: 3, flexShrink: 0, background: tintFor(p.moduleName) }} />
                              <span className="gos-tm-cell">{p.moduleName || '—'}</span>
                            </span>
                          </td>
                          <td>
                            <span className="gos-tm-modchip"><Ic name="lock" size={12} />{p.permissionSetName || '—'}</span>
                          </td>
                          {/* Edit column hidden for now — no per-row edit action
                          <td style={{ textAlign: 'center' }}>
                            <button type="button" className="gos-tm-icon-btn" title="Edit permission" onClick={() => setModal({ record: p })}><Ic name="edit" size={15} /></button>
                          </td>
                          */}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {rows.length === 0 ? <div className="gos-tm-empty">{list.length ? 'No action permissions match your filters.' : 'No action permissions yet — click Add Action Permission.'}</div> : null}
            </>
          )}
        </div>
      </div>

      <ActModal
        open={!!modal}
        record={modal && modal.record}
        unlisted={unlisted}
        moduleList={moduleList}
        setList={permSetList}
        saving={saving}
        onClose={() => setModal(null)}
        onSave={save}
      />
    </Wrap>
  );
}

export default ActionPermissionList;
